'use client';

import { useMemo } from 'react';
import { PageHeader } from '@repo/ui/components/page-header';
import { WalletGate } from '@/components/wallet-gate';
import { describeApiError } from '@/lib/api-client';
import {
  useInvestorLiquidity,
  useInvestorRisk
} from '@/lib/investor-analytics';

import { PortfolioHealthCard } from '../components/portfolio-health-card';
import {
  PortfolioImprovements,
  type ImprovementsEmptyReason
} from '../components/portfolio-improvements';
import { PortfolioRecoveryBanner } from '../components/portfolio-recovery-banner';
import { PortfolioSnapshot } from '../components/portfolio-snapshot';
import { useHoldingCurrencies } from '../hooks/use-holding-currencies';
import { useInvestorHoldings } from '../hooks/use-investor-holdings';
import { useInvestorHygiene } from '../hooks/use-investor-hygiene';
import { useRiskMetrics } from '../hooks/use-risk-metrics';
import { isCompletedHygiene } from '../types/hygiene';
import { valueHoldings } from '../utils/holding-format';
import {
  deriveHealth,
  deriveImprovements,
  type HealthInputs
} from '../utils/derive-health';
import {
  hygieneImprovementsEmptyState,
  hygieneToAssessment,
  hygieneToImprovements
} from '../utils/hygiene-adapter';

/** What a failed currency name lookup changes on screen, in plain words. */
const CURRENCY_NAMES_NOTE =
  'Some currency names could not be loaded, so those currencies show a shortened address.';

export function OverviewView() {
  const holdings = useInvestorHoldings();
  // Names for the strategy currency addresses the snapshot converts.
  const currencyNames = useHoldingCurrencies(holdings.data);
  const currencies = currencyNames.registry;
  const metrics = useRiskMetrics({ window: '12m' });
  // Canonical hygiene assessment — the score authority when the backend
  // serves it. 404 (flag off) falls back to the documented client-side
  // interim below.
  const hygiene = useInvestorHygiene();

  // A cached assessment outlives a failed refresh: the card keeps it and
  // the banner reports the failure.
  const canonical =
    hygiene.data && isCompletedHygiene(hygiene.data) ? hygiene.data : undefined;
  const hygieneSettled =
    hygiene.data !== undefined || hygiene.isError || hygiene.isUnsupported;
  // The derived interim runs in exactly one case: the feature is switched
  // off for this deployment (a plain 404) and no canonical assessment is
  // cached. A refresh that 404s beside a cached assessment leaves that
  // assessment in charge, and the fallback's sources stay off: their loading
  // would hide the score and their failures would join the banner.
  const fallbackActive = hygiene.isUnsupported && !canonical;
  // Any other failure with no assessment cached is "Assessment unavailable"
  // with a retry (docs/features/portfolio-hygiene.md). Deriving a score here
  // painted one number on a 5xx and a different one on the next good poll.
  const isUnavailable = hygiene.isError && !fallbackActive && !canonical;
  // `unavailable`: the backend is preparing this wallet's first assessment
  // (auto-queued; the hook fast-polls). The card shows a labeled waiting
  // state — never interim numbers a completed assessment would replace.
  const isPreparing =
    hygiene.data?.status === 'unavailable' && !hygiene.isError;

  // Fallback-only evidence sources. When canonical is the authority these
  // never fire, cutting Overview's request fan-out in half and keeping
  // their failure modes off a page that no longer depends on them.
  const risk = useInvestorRisk('12m', { enabled: fallbackActive });
  const liquidity = useInvestorLiquidity({ enabled: fallbackActive });

  // The holdings on one stated basis, as the snapshot reads them.
  const valuation = useMemo(
    () => (holdings.data ? valueHoldings(holdings.data, currencies) : null),
    [holdings.data, currencies]
  );

  const healthInputs = useMemo<HealthInputs>(
    () => ({
      holdings: holdings.data
        ? {
            // The same dollar total the snapshot states, and only when it is
            // every holding at its dollar value.
            reconciledTotalUsd: valuation?.total.reconciled
              ? valuation.total.usd
              : null,
            totals: holdings.data.totals,
            coverage: holdings.data.coverage
          }
        : undefined,
      risk: risk.data
        ? {
            concentration: risk.data.concentration,
            vaultConcentration: risk.data.vaultConcentration,
            exposures: { byAsset: risk.data.exposures.byAsset },
            exposureCoverage: risk.data.exposureCoverage
          }
        : undefined,
      // Practices only: realised drawdown and volatility are outcomes and
      // never reach the score (see `derive-health`).
      liquidity: liquidity.data
        ? {
            basis: liquidity.data.basis,
            coverageBps: liquidity.data.coverageBps,
            totalValueUsd: liquidity.data.totalValueUsd,
            // The served liquidity itself: the score reads the same
            // accepted figures as the Risk & liquidity screen.
            served: liquidity.data,
            horizon: liquidity.data.horizon
          }
        : undefined
    }),
    [holdings.data, valuation, risk.data, liquidity.data]
  );

  const assessment = useMemo(
    () =>
      canonical ? hygieneToAssessment(canonical) : deriveHealth(healthInputs),
    [canonical, healthInputs]
  );
  const improvements = useMemo(() => {
    // The improvement rule: canonical cards derive ONLY from scored checks
    // failing their reference — never from warnings, coverage, or wiring.
    // An empty list is the honest answer for a healthy or young wallet.
    if (canonical) return hygieneToImprovements(canonical);
    return deriveImprovements(healthInputs, assessment);
  }, [canonical, healthInputs, assessment]);

  const snapshotQueries = [holdings, metrics];
  const fallbackQueries = [risk, liquidity];
  const failedSources = [
    { name: 'holdings', query: holdings },
    { name: 'metrics', query: metrics },
    ...(fallbackActive
      ? [
          { name: 'risk', query: risk },
          { name: 'liquidity', query: liquidity }
        ]
      : []),
    ...(hygiene.isError && !hygiene.isUnsupported
      ? [{ name: 'hygiene', query: hygiene }]
      : []),
    // Only when a currency needed naming: see `useHoldingCurrencies`.
    ...(currencyNames.lookup
      ? [{ name: 'currency names', query: currencyNames.lookup }]
      : [])
  ].filter((source) => source.query.isError);
  const failedQueries = failedSources.map((source) => source.query);
  const failureDetail =
    failedSources.length > 0
      ? failedSources
          .map(
            (source) =>
              `${source.name}: ${describeApiError(source.query.error) ?? 'failed'}`
          )
          .join(' · ')
      : undefined;
  const hasDataError = failedQueries.length > 0;
  const isRetrying = failedQueries.some((query) => query.isFetching);
  // Hold the skeleton until the assessment is SETTLED. Canonical settles on
  // its own; the derived fallback needs every evidence source answered or
  // terminally failed. A partially derived score must never paint and then
  // shift. Once settled, background refetches never regress to skeletons.
  const fallbackSettled = [...snapshotQueries, ...fallbackQueries].every(
    (query) => query.data !== undefined || query.isError
  );
  const isLoading = !hygieneSettled || (fallbackActive && !fallbackSettled);
  const visibleImprovements =
    isPreparing || isLoading || isUnavailable ? [] : improvements;
  // Why the improvements list is empty, when it is. The green "nothing to
  // improve" is earned only when something was assessed and all of it is
  // inside its guideline.
  const emptyReason: ImprovementsEmptyReason = isUnavailable
    ? 'unavailable'
    : canonical
      ? hygieneImprovementsEmptyState(canonical)
      : [...snapshotQueries, ...fallbackQueries].some(
            (query) => query.isError && query.data === undefined
          )
        ? 'unavailable'
        : assessment.drivers.every((driver) => driver.score == null)
          ? 'unassessed'
          : assessment.drivers.some(
                (driver) =>
                  driver.state === 'watch' || driver.state === 'at-risk'
              )
            ? 'attention'
            : 'all-clear';
  const retryFailed = () => {
    // `cancelRefetch: false` joins a fetch already in flight instead of
    // aborting it and asking a second time.
    void Promise.all(
      failedQueries.map((query) => query.refetch({ cancelRefetch: false }))
    );
  };
  const retryHygiene = () => {
    void hygiene.refetch({ cancelRefetch: false });
  };

  return (
    <WalletGate
      title="Connect to open your overview"
      description="Connect the approved wallet to load your live portfolio health and holdings."
    >
      <div className="space-y-6 p-3 sm:p-4 lg:p-6">
        {hasDataError ? (
          <PortfolioRecoveryBanner
            onRetry={retryFailed}
            isRetrying={isRetrying}
            detail={failureDetail}
            notes={currencyNames.namesMissing ? [CURRENCY_NAMES_NOTE] : []}
          />
        ) : null}

        <PageHeader
          title="Portfolio health"
          description="Understand what is driving the score and model the changes that could improve it."
        />

        <PortfolioHealthCard
          assessment={assessment}
          isLoading={isLoading}
          isPreparing={isPreparing}
          isPreparingSlowly={hygiene.isPreparingSlowly}
          isUnavailable={isUnavailable}
          onRetry={retryHygiene}
          isRetrying={hygiene.isFetching}
          hygiene={canonical}
          snapshot={
            <PortfolioSnapshot
              holdings={holdings.data}
              currencies={currencies}
              metrics={metrics.data}
              holdingsLoading={holdings.isLoading}
              holdingsError={holdings.isError && !holdings.data}
              metricsLoading={metrics.isLoading}
              metricsError={metrics.isError && !metrics.data}
            />
          }
        />
        <PortfolioImprovements
          improvements={visibleImprovements}
          isLoading={isLoading || isPreparing}
          emptyReason={emptyReason}
        />
      </div>
    </WalletGate>
  );
}
